import type {
  ContentfulHeadlineBlock,
  ContentfulLinkAction,
  ContentfulMediaImage,
  ContentfulTextBlock,
} from "@scott/contentful/types";
import type { TextImageProps } from "@/scott/ui/blocks/text-image/text-image";
import { adaptButton } from "@/services/contentful/assemblies/page/adapter/button-adapter";
import {
  extractCloudinaryAssetUrl,
  getBlockId,
  getCloudinarySquareSrcSet,
} from "@/services/contentful/assemblies/page/adapter/utils";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type { SectionTextImagePageBlock } from "@/types/page-block";

const columnWidthMap = {
  narrow: 8,
  medium: 10,
  wide: 12,
} as const satisfies Record<
  NonNullable<SectionTextImageInput["containerWidth"]>,
  NonNullable<TextImageProps["columnWidth"]>
>;

export interface SectionTextImageInput {
  sys?: { id: string };
  __typename: "SectionTextImage";
  internalName: string;
  containerWidth?: "narrow" | "medium" | "wide";
  orderReverse: boolean;
  headline?: ContentfulHeadlineBlock;
  text?: ContentfulTextBlock;
  uiConfiguration?: UiConfigurationInput;
  media?: ContentfulMediaImage;
  cta?: ContentfulLinkAction;
}

export function SectionTextImageAdapter(
  item: SectionTextImageInput,
  index: number,
  basePath?: string
): SectionTextImagePageBlock {
  const image = (() => {
    if (item.media?.__typename !== "MediaImage") {
      return;
    }

    const src = extractCloudinaryAssetUrl(item.media.image);
    const square = getCloudinarySquareSrcSet(src);

    return {
      sys: item.media.sys,
      src: square?.src ?? src,
      srcSet: square?.srcSet,
      alt: item.media.alternativeText,
    };
  })();

  const button = item.cta ? adaptButton(item.cta, basePath) : undefined;
  const uiConfig = item.uiConfiguration
    ? UiConfigurationAdapter(item.uiConfiguration)
    : undefined;

  return {
    sys: item.sys,
    id: getBlockId(item, index),
    __typename: item.__typename,
    internalName: item.internalName,
    text: item.text,
    headline: item.headline,
    orderReverse: item.orderReverse,
    columnWidth: item.containerWidth ? columnWidthMap[item.containerWidth] : 10,
    image,
    cta: button,
    backgroundColor: uiConfig?.backgroundColor,
    headlineColor: uiConfig?.headlineColor,
    color: uiConfig?.textColor,
    paddingTop: uiConfig?.paddingTop,
    paddingBottom: uiConfig?.paddingBottom,
  };
}
